import { newError } from '#app/lib/error'
import type { UserLang } from '#app/lib/languages/languages'
import { currentRouteWithQueryStringAndHash, parseQuery, type ProjectRootRelativeUrl } from '#app/lib/location'
import { logError } from '#app/lib/loggers'
import { clearMetadata, updateRouteMetadata, type MetadataUpdate } from '#app/lib/metadata/update'
import { keepQuerystringParameter } from '#app/lib/querystring_helpers'
import { getLastLoadedUrlHash, getLastLoadedUrlWithoutHash, loadUrl, startRouter } from '#app/lib/router'
import { scrollToElement, scrollToSelector } from '#app/lib/screen'
import { toDomSelector } from '#app/lib/utils'
import { commands, vent } from '#app/radio'
import { updateI18nLang } from '#user/lib/i18n'

let initialUrlNavigateAlreadyCalled = false
let lastNavigateTimestamp = 0

interface NavigateOptions {
  replace?: boolean
  trigger?: boolean
  pageSectionElement?: HTMLElement
  preventScrollTop?: boolean
  metadata?: MetadataUpdate | Promise<MetadataUpdate>
}

const historyLast: string[] = []

async function navigate (route: string, options: NavigateOptions = {}) {
  if (route[0] !== '/') route = `/${route}`
  // Close the modal if it was open
  commands.execute('modal:close')
  // Update metadata before testing if the route changed
  // so that a call from a router action would trigger a metadata update
  // but not affect the history (due to the early return hereafter)
  const metadata = 'metadata' in options ? options.metadata : {}
  updateRouteMetadata(route, metadata)

  let [ routeWithoutHash, hash ] = route.split('#')
  if (!hash) {
    // Recover the hash that was passed to loadUrl, but lost when the layout re-called app.navigate without hash
    hash = getLastLoadedUrlHash(routeWithoutHash)
    if (hash) route += `#${hash}`
  }

  // Easing code mutualization by firing app.navigate, even when the module
  // simply reacted to the requested URL
  if (route === currentRouteWithQueryStringAndHash()) {
    // Trigger a route event for the first URL, so that event listeners can update accordingly
    if (!initialUrlNavigateAlreadyCalled) {
      vent.trigger('route:change', route)
      initialUrlNavigateAlreadyCalled = true
    }
    if (hash) {
      await scrollToFragment(hash)
    } else if (options.trigger && !options.preventScrollTop) {
      scrollToPageTop()
    }
    return
  }

  vent.trigger('route:change', route)
  route = keepQuerystringParameter(route)

  const { pageSectionElement, preventScrollTop } = options
  if (pageSectionElement) {
    scrollToElement(pageSectionElement)
  } else if (!preventScrollTop) {
    scrollToPageTop()
  }

  historyLast.unshift(route)

  // Replace last route in history when several navigation happen quickly
  // so that hitting "Previous" correctly brings back to the last page
  // where a user action triggered a page change
  const now = Date.now()
  if (now < lastNavigateTimestamp + 200) options.replace = true
  lastNavigateTimestamp = now

  if (options.replace) {
    history.replaceState(null, '', route)
  } else {
    history.pushState(null, '', route)
  }
  if (options.trigger) await loadUrl(route)

  if (hash) await scrollToFragment(hash)
}

async function scrollToFragment (hash: string) {
  const selector = `#${toDomSelector(hash)}`
  // Fix deep linking for dynamically rendered HTML
  await scrollToSelector(selector, { highlight: true, focus: true })
}

function navigateAndLoad (route: ProjectRootRelativeUrl, options: NavigateOptions = {}) {
  options.trigger = true
  navigate(route, options)
}

// Used by firefox-headless-prerender
async function clearMetadataNavigateAndLoad (route: ProjectRootRelativeUrl, options: NavigateOptions = {}) {
  clearMetadata()
  const { lang = 'en' } = parseQuery(route.split('?')[1])
  await updateI18nLang(lang as UserLang)
  navigateAndLoad(route, options)
}

function navigateReplace (route: ProjectRootRelativeUrl, options: NavigateOptions = {}) {
  options.replace = true
  navigate(route, options)
}

function navigateToFragment (fragmentId: string) {
  const { pathname, search } = location
  const route = `${pathname}${search}#${fragmentId}`
  return navigate(route, { preventScrollTop: true })
}

const app = {
  navigate,
  navigateAndLoad,
  clearMetadataNavigateAndLoad,
  navigateReplace,
  navigateToFragment,
  start,
} as const

async function start () {
  // Import async to let AppLayout dependencies depend on the app object
  const { initAppLayout } = await import('#app/init_app_layout.svelte.ts')
  initAppLayout()
  await startRouter()
  window.addEventListener('popstate', navigateOnPopState)
}

// Triggered at initial page load and when the user goes backward or forward in history
// NB: if after going backward, it's not possible to go forward, it's likely because
// a call to "pushState" forked the browser history
async function navigateOnPopState (event: PopStateEvent) {
  if (event.target && 'location' in event.target) {
    const { pathname, search, hash } = event.target.location as Location
    const newRouteWithoutHash = `${pathname}${search}`
    const lastLoadedUrlWithoutHash = getLastLoadedUrlWithoutHash()
    if (newRouteWithoutHash === lastLoadedUrlWithoutHash) {
      // Here we could scroll to the fragment, but apparently the browser already does it
    } else {
      const route = `${pathname}${search}${hash}`
      await loadUrl(route)
    }
    vent.trigger('route:change', pathname)
  } else {
    logError(newError('can not navigate back', 500, { event }))
  }
}

export function canGoBack () {
  return historyLast.length > 1
}

// Required by firefox-headless-prerender
window.app = app

export default app

const scrollToPageTop = () => window.scrollTo(0, 0)
